/**
 * « Mes agents » (/agents) and one agent (/agents/$id): several agents per account, each with its own conversation,
 * personality, face and cloud computer, sharing one memory. On iMessage, « @Nom » at the start of a message talks to
 * that agent; the agents also talk to each other (the threads below).
 */
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useId, useLayoutEffect, useState, type FormEvent, type ReactNode } from "react";
import type { BotThreadView, BotView } from "../../shared/bots";
import { chatUiFallbackText } from "../../shared/chat-ui";
import { DEFAULT_SETTINGS, MACHINE_SIZES, PERSONA_VALUES, type ChatMessage, type MachineSizeName, type Persona } from "../../shared/contracts";
import {
  AgentAvatarView,
  CHARACTER_COLOR_HEX,
  CHARACTER_COLOR_LABEL,
  CHARACTER_COLOR_LIST,
  CHARACTER_SHAPE_LABEL,
  CHARACTER_SHAPE_LIST,
  lookOf,
  type CharacterColor,
  type CharacterShape,
} from "../components/avatar";
import { AvatarStudio } from "../components/avatar-studio";
import { bubbleRadius, BUBBLE_TEXT, dayLabel, DaySeparator, Row, sameGroup, startOfDay, TypingDots } from "../components/conversation";
import { IconArrowUp, IconBack, IconGrid, IconPlus, IconTrash, IconTune } from "../components/icons";
import { RichText } from "../components/rich-text";
import { useShell } from "../components/shell";
import { Badge, Button, Card, cx, ErrorNotice, Field, Input, Loading, Notice, PageTop, Panel, RadioCard, ROUND_BUTTON, Section, Select, Textarea } from "../components/ui";
import { api, errorText } from "../lib/api";
import { machineStateLabel, PERSONAS, sizeLabel } from "../lib/format";
import { clientId, useNow, usePageTitle } from "../lib/hooks";
import { invalidate, useResource } from "../lib/store";
import { CloudScreen } from "./computer";

const personaLabel = (p: Persona) => PERSONAS.find((x) => x.value === p)?.label ?? p;

function useBots() {
  return useResource("bots", (signal) => api("GET /api/bots", { signal }));
}

function useThreads() {
  return useResource("bots:threads", (signal) => api("GET /api/bots/threads", { signal }), { pollMs: 8_000 });
}

/** /agents — the account's agents, a new one, and what they said to each other. */
export function AgentsPage() {
  usePageTitle("Mes agents");
  const bots = useBots();
  const threads = useThreads();
  if (bots.error && !bots.data) return <ErrorNotice message={errorText(bots.error)} onRetry={() => void bots.reload()} />;
  if (!bots.data) return <Loading />;
  const extra = bots.data.bots.filter((b) => !b.main).length;
  return (
    <div className="mx-auto flex max-w-[680px] flex-col gap-6">
      <PageTop title="Mes agents" className="mb-0" />
      <ul className="grid gap-2 sm:grid-cols-2" aria-label="Agents">
        {bots.data.bots.map((b) => (
          <li key={b.id}>
            <AgentCard bot={b} />
          </li>
        ))}
      </ul>
      {extra < bots.data.max ? <CreateAgent existing={bots.data.bots.map((b) => b.name)} /> : <Notice>{bots.data.max} agents en plus du principal, au plus.</Notice>}
      <Section title="Entre agents">
        <Threads threads={threads.data?.threads ?? []} />
      </Section>
    </div>
  );
}

function AgentCard({ bot }: { bot: BotView }) {
  return (
    <Link
      to={bot.main ? "/" : "/agents/$id"}
      params={bot.main ? undefined : { id: bot.id }}
      data-testid="agent-card"
      className="flex items-center gap-3.5 rounded-[20px] bg-elev p-4 transition hover:bg-chip"
    >
      <AgentAvatarView agent={{ name: bot.name, avatar: bot.avatar, character: bot.character, main: bot.main }} size={48} bare />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-[16px] font-semibold">{bot.name}</span>
          {bot.main ? <Badge tone="neutral">Principal</Badge> : null}
        </span>
        <span className="block truncate text-[13px] text-muted">{personaLabel(bot.persona)}</span>
      </span>
    </Link>
  );
}

function CreateAgent({ existing }: { existing: string[] }) {
  const navigate = useNavigate();
  const ids = { name: useId(), notes: useId() };
  const [name, setName] = useState("");
  const [persona, setPersona] = useState<Persona>("direct");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const n = name.trim();
    if (!n) return setError("Donne un nom à ton agent.");
    if (existing.some((x) => x.toLocaleLowerCase("fr") === n.toLocaleLowerCase("fr"))) return setError("Un de tes agents porte déjà ce nom.");
    setBusy(true);
    setError(null);
    try {
      const bot = await api("POST /api/bots", { body: { name: n, persona, ...(notes.trim() ? { personaNotes: notes.trim() } : {}) } });
      invalidate("bots");
      void navigate({ to: "/agents/$id", params: { id: bot.id } });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Créer un agent">
      <form onSubmit={submit} className="space-y-4" aria-label="Créer un agent">
        <Field label="Son nom" htmlFor={ids.name} hint="Sur iMessage : « @Nom … »." error={error}>
          <Input id={ids.name} value={name} maxLength={40} onChange={(e) => setName(e.target.value)} placeholder="Atlas" autoComplete="off" />
        </Field>
        <fieldset className="space-y-2">
          <legend className="text-[13px] font-medium">Sa personnalité</legend>
          <div className="grid gap-2 sm:grid-cols-3">
            {PERSONAS.filter((p) => (PERSONA_VALUES as readonly string[]).includes(p.value)).map((p) => (
              <RadioCard key={p.value} name="new-persona" value={p.value} label={p.label} description={p.description} checked={persona === p.value} onChange={() => setPersona(p.value)} />
            ))}
          </div>
        </fieldset>
        <Field label="Son rôle (facultatif)" htmlFor={ids.notes}>
          <Textarea id={ids.notes} value={notes} maxLength={1000} rows={2} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <Button type="submit" busy={busy}>
          <IconPlus width={16} height={16} />
          Créer l'agent
        </Button>
      </form>
    </Panel>
  );
}

const KIND_LABEL: Record<string, string> = { ask: "demande", tell: "confie", reply: "répond", refused: "arrêté" };

function Threads({ threads }: { threads: BotThreadView[] }) {
  if (!threads.length) return <p className="px-1 text-[14px] text-muted">Pas encore d'échange.</p>;
  return (
    <ul className="space-y-2" aria-label="Échanges entre agents">
      {threads.slice(0, 20).map((t) => (
        <li key={t.id}>
          <Card className="space-y-2 p-4" data-testid="bot-thread">
            {t.messages.map((m) => (
              <div key={m.id} className="text-[13.5px]">
                <span className="font-medium">{m.from.name}</span> <span className="text-subtle">{KIND_LABEL[m.kind] ?? m.kind} à</span>{" "}
                <span className="font-medium">{m.to.name}</span>
                {m.kind === "refused" ? (
                  <Badge tone="warning" className="ml-2">
                    boucle évitée
                  </Badge>
                ) : null}
                <p className="mt-0.5 whitespace-pre-wrap text-muted">{m.text}</p>
              </div>
            ))}
          </Card>
        </li>
      ))}
    </ul>
  );
}

/**
 * /agents/$id — an extra agent: its conversation (the V2 bubbles; « #reglages » or « #ordinateur » open its settings
 * instead: its computer, personality, face, deletion).
 */
export function AgentDetailPage({ id }: { id: string }) {
  // Its computer reports to this agent, not to the main agent's live channel: while it is on, the page polls its state.
  const res = useResource(`bot:${id}`, (signal) => api("GET /api/bots/:botId", { params: { botId: id }, signal }), {
    pollMs: (data?: BotView) => (data?.computer && data.computer.state !== "stopped" ? 5_000 : 30_000),
  });
  const hash = useRouterState({ select: (s) => s.location.hash });
  const [view, setView] = useState<"chat" | "reglages">(hash === "reglages" || hash === "ordinateur" ? "reglages" : "chat");
  useEffect(() => {
    if (hash === "reglages" || hash === "ordinateur") setView("reglages");
  }, [hash]);
  usePageTitle(res.data?.name ?? "Agent");
  if (res.error && !res.data) return <ErrorNotice message={errorText(res.error)} onRetry={() => void res.reload()} />;
  if (!res.data) return <Loading />;
  const bot = res.data;
  if (view === "chat") return <BotConversation bot={bot} onSettings={() => setView("reglages")} />;
  return (
    <div className="mx-auto flex w-full max-w-[720px] flex-col gap-5 px-4 pt-3.5 pb-12 md:px-6 md:pt-[18px]">
      <header className="flex min-h-11 items-center gap-3">
        <button type="button" aria-label="Retour" onClick={() => setView("chat")} className={ROUND_BUTTON}>
          <IconBack width={16} height={16} strokeWidth={2.4} />
        </button>
        <AgentAvatarView agent={{ name: bot.name, avatar: bot.avatar, character: bot.character, main: false }} size={30} bare />
        <h1 className="min-w-0 flex-1 truncate font-display text-[28px] leading-tight font-medium">{bot.name}</h1>
        <Link to="/agents" className="inline-flex h-9 items-center rounded-full bg-chip px-3.5 text-[13.5px] font-semibold max-md:h-11">
          Mes agents
        </Link>
      </header>
      <BotComputer bot={bot} onChange={(next) => res.mutate(() => next)} />
      <BotSettings bot={bot} onChange={(next) => res.mutate(() => next)} />
      <Panel title="Son visage">
        <BotFace bot={bot} onChange={(next) => res.mutate(() => next)} />
        <AvatarStudio
          agentId={bot.id}
          settings={{ ...DEFAULT_SETTINGS, agentName: bot.name, avatar: bot.avatar, character: bot.character }}
          onSaved={(s) => {
            res.mutate((b) => (b ? { ...b, avatar: s.avatar } : b));
            invalidate("bots");
          }}
        />
      </Panel>
      <DeleteBot bot={bot} />
    </div>
  );
}

/** An extra agent's face: its body and color (PATCH /api/bots/:botId { character }). */
function BotFace({ bot, onChange }: { bot: BotView; onChange: (b: BotView) => void }) {
  const look = lookOf({ name: bot.name, character: bot.character, main: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save(next: { shape: CharacterShape; color: CharacterColor }) {
    setBusy(true);
    setError(null);
    try {
      onChange(await api("PATCH /api/bots/:botId", { params: { botId: bot.id }, body: { character: next } }));
      invalidate("bots");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex flex-wrap items-center gap-4">
      <AgentAvatarView agent={{ name: bot.name, avatar: null, character: { shape: look.shape, color: look.color }, main: false }} size={64} bare />
      <div className="flex flex-col gap-2.5">
        <div role="group" aria-label="Forme" className="flex gap-1.5">
          {CHARACTER_SHAPE_LIST.map((shape) => (
            <button
              key={shape}
              type="button"
              disabled={busy}
              aria-pressed={look.shape === shape}
              onClick={() => void save({ shape, color: look.color })}
              className={cx("h-9 rounded-full px-3 text-[13.5px] font-medium max-md:h-11", look.shape === shape ? "bg-ink text-ink-fg" : "bg-chip text-fg")}
            >
              {CHARACTER_SHAPE_LABEL[shape]}
            </button>
          ))}
        </div>
        <div role="group" aria-label="Couleur" className="flex flex-wrap gap-2">
          {CHARACTER_COLOR_LIST.map((color) => (
            <button
              key={color}
              type="button"
              disabled={busy}
              aria-pressed={look.color === color}
              aria-label={CHARACTER_COLOR_LABEL[color]}
              onClick={() => void save({ shape: look.shape, color })}
              className="h-8 w-8 rounded-full max-md:h-11 max-md:w-11"
              style={{ background: CHARACTER_COLOR_HEX[color], boxShadow: look.color === color ? "0 0 0 3px var(--bg-elev), 0 0 0 5px var(--fg)" : undefined }}
            />
          ))}
        </div>
      </div>
      {error ? <Notice tone="danger">{error}</Notice> : null}
    </div>
  );
}

/** An extra agent's conversation, in the V2 bubbles (its own thread: GET / POST /api/bots/:botId/chat, polled). */
function BotConversation({ bot, onSettings }: { bot: BotView; onSettings: () => void }) {
  const shell = useShell();
  const [waitingUntil, setWaitingUntil] = useState(0);
  const chat = useResource(`bot-chat:${bot.id}`, (signal) => api("GET /api/bots/:botId/chat", { params: { botId: bot.id }, query: { limit: "50" }, signal }), {
    pollMs: () => (Date.now() < waitingUntil ? 1_500 : 10_000),
  });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputId = useId();
  const now = useNow(60_000);
  const agent = { name: bot.name, avatar: bot.avatar, character: bot.character, main: false };
  const messages: ChatMessage[] = (chat.data?.messages ?? []).filter((m) => !m.reaction);
  const waiting = Date.now() < waitingUntil && messages[messages.length - 1]?.direction !== "out";

  useLayoutEffect(() => {
    window.scrollTo({ top: document.documentElement.scrollHeight });
  }, [messages.length, waiting]);

  async function send(e: FormEvent) {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    setError(null);
    try {
      await api("POST /api/bots/:botId/chat", { params: { botId: bot.id }, body: { text: t, clientId: clientId() } });
      setText("");
      setWaitingUntil(Date.now() + 90_000);
      void chat.reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  let prev: { out: boolean; at: number } | null = null;
  let lastDay = -1;
  const rows: ReactNode[] = [];
  messages.forEach((m, i) => {
    const d = startOfDay(m.createdAt);
    if (d !== lastDay) {
      lastDay = d;
      prev = null;
      rows.push(<DaySeparator key={`day-${d}`} label={dayLabel(m.createdAt, now)} />);
    }
    const out = m.direction === "out";
    const cur = { out, at: m.createdAt };
    const next = messages[i + 1];
    const first = !sameGroup(prev, cur);
    const last = !next || !sameGroup(cur, { out: next.direction === "out", at: next.createdAt });
    prev = cur;
    rows.push(
      <Row
        key={m.id}
        out={out}
        first={first}
        last={last}
        agent={agent}
        testId={out ? "bot-msg-out" : "bot-msg-in"}
        bubble={
          <div className={cx("min-w-0 max-w-[560px] text-fg wrap-anywhere", out ? "bg-bubble-agent" : "bg-bubble-user whitespace-pre-wrap", BUBBLE_TEXT, bubbleRadius(out, last))}>
            {/* This thread shows a generated interface (show_ui) as its plain-text version. */}
            {out ? <RichText text={m.ui ? [m.text.trim(), chatUiFallbackText(m.ui)].filter(Boolean).join("\n\n") : m.text} /> : m.text}
          </div>
        }
      />,
    );
  });

  return (
    <div className="flex min-h-[calc(100dvh-96px)] w-full flex-col md:min-h-dvh">
      <div className="pointer-events-none sticky top-0 z-20 flex flex-col items-center gap-1.5 pt-3.5 max-md:pt-1">
        <div className="pointer-events-auto flex items-center gap-1.5 rounded-[20px] bg-elev py-[5px] pr-[5px] pl-1.5">
          <AgentAvatarView agent={agent} size={26} bare mood={waiting ? "reflechit" : "repos"} />
          <h1 className="pr-1.5 text-[14px] font-semibold">{bot.name}</h1>
          <Link to="/agents" aria-label="Mes agents" className="flex h-7 w-7 items-center justify-center rounded-full bg-chip text-muted hover:text-fg max-md:h-9 max-md:w-9">
            <IconGrid width={14} height={14} strokeWidth={2} />
          </Link>
          <button type="button" aria-label="Ses réglages" onClick={onSettings} className="flex h-7 w-7 items-center justify-center rounded-full bg-chip text-muted hover:text-fg max-md:h-9 max-md:w-9">
            <IconTune width={14} height={14} strokeWidth={2} />
          </button>
        </div>
        {/* Its computer is on: its live screen (the « Écran en direct » link its agent sends points to this page) is one tap away. */}
        {bot.computer?.liveView && bot.computer.state !== "stopped" ? (
          <button
            type="button"
            onClick={onSettings}
            className="pointer-events-auto inline-flex h-8 items-center gap-1.5 rounded-full bg-elev px-3 text-[13px] font-semibold text-fg transition hover:bg-chip max-md:h-11"
          >
            <span aria-hidden="true" className="live-dot h-[7px] w-[7px] rounded-full bg-accent" />
            Son ordinateur
          </button>
        ) : null}
      </div>
      <div className="mx-auto flex w-full max-w-[784px] flex-1 flex-col justify-end px-3 pt-1.5 pb-2.5 md:px-8">
        {!messages.length ? (
          <div className="mx-auto mb-4 flex flex-col items-center gap-3 text-center">
            <AgentAvatarView agent={agent} size={96} bare />
            <p className="font-display text-[24px]">Dis bonjour à {bot.name}</p>
          </div>
        ) : null}
        <div role="log" aria-label={`Conversation avec ${bot.name}`} className="flex flex-col pb-2">
          {rows}
          {waiting ? (
            <Row
              out
              first
              last
              agent={agent}
              mood="reflechit"
              bubble={
                <div className={cx("bg-bubble-agent px-4 py-3", bubbleRadius(true, true))}>
                  <TypingDots label={`${bot.name} écrit`} />
                </div>
              }
            />
          ) : null}
        </div>
      </div>
      <form onSubmit={send} className="sticky bottom-0 z-10 bg-bg px-2.5 pt-1.5 pb-[calc(env(safe-area-inset-bottom)+14px)] md:px-8 md:pb-5">
        <div className="mx-auto flex w-full max-w-[720px] items-end gap-2">
          {shell ? (
            <button type="button" aria-label={`${bot.name}, réglages`} onClick={shell.openAgentSheet} className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full bg-elev shadow-[inset_0_0_0_1px_var(--border)] md:hidden">
              <AgentAvatarView agent={agent} size={30} bare />
            </button>
          ) : null}
          <div className="flex min-w-0 flex-1 items-end gap-1.5 rounded-[23px] bg-elev py-[3px] pr-[3px] pl-4 shadow-[inset_0_0_0_1px_var(--border)] md:rounded-3xl md:py-[5px] md:pr-[5px]">
            <label htmlFor={inputId} className="sr-only">
              Message à {bot.name}
            </label>
            <textarea
              id={inputId}
              rows={1}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  e.currentTarget.form?.requestSubmit();
                }
              }}
              placeholder={`Écris à ${bot.name}`}
              className="max-h-[150px] min-h-10 min-w-0 flex-1 resize-none bg-transparent py-[9px] text-[16px] leading-[1.4] text-fg outline-none placeholder:text-muted md:text-[15.5px]"
            />
            <button type="submit" disabled={busy || !text.trim()} aria-busy={busy || undefined} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ink text-ink-fg disabled:opacity-40 md:h-10 md:w-10">
              <IconArrowUp width={18} height={18} strokeWidth={2.4} />
              <span className="sr-only">Envoyer</span>
            </button>
          </div>
        </div>
        {error ? <p className="mx-auto mt-1.5 max-w-[720px] px-2 text-[12.5px] text-danger">{error}</p> : null}
      </form>
    </div>
  );
}

function BotComputer({ bot, onChange }: { bot: BotView; onChange: (b: BotView) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sizeId = useId();
  const computer = bot.computer;

  async function act(work: () => Promise<BotView["computer"]>) {
    setBusy(true);
    setError(null);
    try {
      onChange({ ...bot, computer: await work() });
      invalidate("bots");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Son ordinateur" description="Un ordinateur cloud à lui, qui s'éteint tout seul quand il ne sert pas.">
      {computer?.liveView ? (
        // Its screen (every desktop of its computer) and the same controls bar as /ordinateur: « Prendre les commandes »
        // hands the owner mouse and keyboard and makes this agent wait; « Rendre la main » gives them back.
        <CloudScreen
          machine={computer}
          autoConnect={false}
          agent={{ name: bot.name, avatar: bot.avatar, accentColor: bot.accentColor }}
          onMachine={(m) => {
            onChange({ ...bot, computer: m });
            invalidate("bots");
          }}
        />
      ) : null}
      {computer ? (
        <div className="space-y-3">
          <p className="text-[14px]">
            {computer.name} · <Badge tone={computer.state === "running" ? "success" : "neutral"}>{machineStateLabel(computer.state)}</Badge> · {sizeLabel(computer.size)}
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Puissance" htmlFor={sizeId}>
              <Select
                id={sizeId}
                value={typeof computer.size === "string" ? computer.size : "standard-1"}
                onChange={(e) => void act(() => api("PATCH /api/machines/:id", { params: { id: computer.id }, body: { size: e.target.value as MachineSizeName } }))}
                disabled={busy}
              >
                {(Object.keys(MACHINE_SIZES) as MachineSizeName[]).map((s) => (
                  <option key={s} value={s}>
                    {MACHINE_SIZES[s].label}
                  </option>
                ))}
              </Select>
            </Field>
            {computer.state === "stopped" ? (
              <Button variant="secondary" busy={busy} onClick={() => void act(() => api("POST /api/machines/:id/start", { params: { id: computer.id }, body: {} }))}>
                Allumer
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <Button variant="secondary" busy={busy} onClick={() => void act(() => api("POST /api/bots/:botId/computer", { params: { botId: bot.id }, body: {} }))}>
          Créer son ordinateur
        </Button>
      )}
      {error ? <Notice tone="danger">{error}</Notice> : null}
    </Panel>
  );
}

function BotSettings({ bot, onChange }: { bot: BotView; onChange: (b: BotView) => void }) {
  const ids = { name: useId(), notes: useId() };
  const [name, setName] = useState(bot.name);
  const [persona, setPersona] = useState<Persona>(bot.persona);
  const [notes, setNotes] = useState(bot.personaNotes);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(null);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      const next = await api("PATCH /api/bots/:botId", { params: { botId: bot.id }, body: { agentName: name.trim(), persona, personaNotes: notes.trim() } });
      onChange(next);
      invalidate("bots");
      setMessage({ tone: "success", text: "Enregistré." });
    } catch (err) {
      setMessage({ tone: "danger", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Sa personnalité">
      <form onSubmit={save} className="space-y-4" aria-label="Sa personnalité">
        <Field label="Son nom" htmlFor={ids.name}>
          <Input id={ids.name} value={name} maxLength={40} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="grid gap-2 sm:grid-cols-3">
          {PERSONAS.map((p) => (
            <RadioCard key={p.value} name={`persona-${bot.id}`} value={p.value} label={p.label} checked={persona === p.value} onChange={() => setPersona(p.value)} />
          ))}
        </div>
        <Field label="Son rôle" htmlFor={ids.notes}>
          <Textarea id={ids.notes} value={notes} maxLength={1000} rows={2} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <Button type="submit" busy={busy}>
          Enregistrer
        </Button>
        {message ? <Notice tone={message.tone}>{message.text}</Notice> : null}
      </form>
    </Panel>
  );
}

function DeleteBot({ bot }: { bot: BotView }) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api("DELETE /api/bots/:botId", { params: { botId: bot.id } });
      invalidate("bots");
      void navigate({ to: "/agents" });
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }
  return (
    <Panel title={`Supprimer ${bot.name}`} description="Sa conversation et son ordinateur sont effacés. Ce qu'il a appris reste dans la mémoire partagée.">
      {confirming ? (
        <div className="flex flex-wrap gap-2">
          <Button variant="danger" busy={busy} onClick={() => void remove()}>
            <IconTrash width={16} height={16} />
            Oui, supprimer {bot.name}
          </Button>
          <Button variant="secondary" onClick={() => setConfirming(false)}>
            Annuler
          </Button>
        </div>
      ) : (
        <Button variant="secondary" onClick={() => setConfirming(true)}>
          Supprimer cet agent
        </Button>
      )}
      {error ? <Notice tone="danger">{error}</Notice> : null}
    </Panel>
  );
}
